<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button class="btn btn-secondary" @click="handleBack">
        <i class="ri-arrow-left-line"></i>
        返回
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回</button>
    </div>

    <div v-else-if="batchIssuance" class="detail-container">
      <!-- 基本資訊 -->
      <div class="detail-section">
        <h2 class="section-title">基本資訊</h2>
        <dl class="info-list">
          <div class="info-row">
            <dt>批次 ID</dt>
            <dd><code class="code-badge">{{ batchIssuance.batchId }}</code></dd>
          </div>
          <div class="info-row">
            <dt>優惠券模板</dt>
            <dd>{{ batchIssuance.templateName }}</dd>
          </div>
          <div class="info-row">
            <dt>每位對象發放數量</dt>
            <dd>{{ batchIssuance.quantityPerMember }} 張</dd>
          </div>
          <div class="info-row">
            <dt>發放原因</dt>
            <dd>{{ batchIssuance.reason || '-' }}</dd>
          </div>
          <div class="info-row">
            <dt>對象類型</dt>
            <dd>{{ isFriendTarget(batchIssuance) ? 'LINE 好友' : '會員' }}</dd>
          </div>
          <div v-if="batchIssuance.targetData && batchIssuance.targetData.csvFileUrl" class="info-row">
            <dt>來源名單檔</dt>
            <dd>
              <a :href="batchIssuance.targetData.csvFileUrl" target="_blank" rel="noopener">
                <i class="ri-file-text-line"></i>
                {{ batchIssuance.targetData.csvFileName || '下載 CSV 檔案' }}
              </a>
            </dd>
          </div>
          <div class="info-row">
            <dt>狀態</dt>
            <dd>
              <span :class="getStatusBadgeClass(batchIssuance.status)">
                {{ getStatusText(batchIssuance.status) }}
              </span>
            </dd>
          </div>
          <div class="info-row">
            <dt>建立時間</dt>
            <dd>{{ formatDateTime(batchIssuance.createdAt) }}</dd>
          </div>
          <div class="info-row">
            <dt>發送者</dt>
            <dd>{{ getCreatedByText(batchIssuance) }}</dd>
          </div>
        </dl>
      </div>

      <!-- 執行結果 -->
      <div v-if="batchIssuance.status === 'completed' || batchIssuance.status === 'processing' || batchIssuance.status === 'failed'" class="detail-section">
        <h2 class="section-title">執行結果</h2>
        <dl class="info-list">
          <div class="info-row">
            <dt>{{ isFriendTarget(batchIssuance) ? '總好友數' : '總會員數' }}</dt>
            <dd>{{ batchIssuance.totalMembers || 0 }} 人</dd>
          </div>
          <div class="info-row">
            <dt>總發放數</dt>
            <dd>{{ batchIssuance.totalCoupons || 0 }} 張</dd>
          </div>
          <div class="info-row">
            <dt>成功 / 失敗</dt>
            <dd>
              <span class="text-success">{{ batchIssuance.successCount || 0 }} 筆成功</span>
              <span class="divider">/</span>
              <span class="text-danger">{{ batchIssuance.failedCount || 0 }} 筆失敗</span>
            </dd>
          </div>
          <div v-if="batchIssuance.completedAt" class="info-row">
            <dt>完成時間</dt>
            <dd>{{ formatDateTime(batchIssuance.completedAt) }}</dd>
          </div>
          <div v-if="batchIssuance.executionDuration" class="info-row">
            <dt>執行耗時</dt>
            <dd>{{ batchIssuance.executionDuration }} 秒</dd>
          </div>
          <div v-if="batchIssuance.errorSummary" class="info-row">
            <dt>錯誤摘要</dt>
            <dd class="error-text">{{ batchIssuance.errorSummary }}</dd>
          </div>
        </dl>
      </div>

      <!-- 撤銷資訊 -->
      <div v-if="batchIssuance.revokedAt" class="detail-section revoke-section">
        <h2 class="section-title">
          <i class="ri-forbidden-line"></i>
          撤銷資訊
        </h2>
        <dl class="info-list">
          <div class="info-row">
            <dt>撤銷時間</dt>
            <dd>{{ formatDateTime(batchIssuance.revokedAt) }}</dd>
          </div>
          <div class="info-row">
            <dt>已撤銷張數</dt>
            <dd>
              <span class="revoke-count">{{ batchIssuance.revokedCount || 0 }} 張</span>
              <span class="revoke-note">（已使用的票券不受影響）</span>
            </dd>
          </div>
        </dl>
      </div>

      <!-- 操作按鈕 -->
      <div class="detail-actions">
        <button 
          v-if="batchIssuance.status === 'completed' || batchIssuance.status === 'failed'"
          class="btn btn-secondary" 
          @click="showDetails = !showDetails"
        >
          <i class="ri-file-list-line"></i>
          {{ showDetails ? '隱藏' : '查看' }}發送明細
        </button>
      </div>

      <!-- 發送明細 -->
      <div v-if="showDetails" class="detail-section">
        <h2 class="section-title">發送明細</h2>
        
        <div class="details-toolbar">
          <div class="search-box">
            <input
              v-model="detailsSearchQuery"
              type="text"
              class="form-control"
              :placeholder="isFriendTarget(batchIssuance) ? '搜尋 LINE User ID...' : '搜尋會員姓名、電話或 Email...'"
              @input="handleDetailsSearch"
            />
            <i class="ri-search-line"></i>
          </div>
          <select v-model="detailsFilterStatus" class="form-control" @change="handleDetailsFilterChange">
            <option value="">全部狀態</option>
            <option value="success">成功</option>
            <option value="failed">失敗</option>
          </select>
        </div>

        <div v-if="detailsLoading" class="loading-state">
          <div class="spinner"></div>
          <p>載入中...</p>
        </div>
        <div v-else-if="detailsError" class="error-state">
          <p class="error-message">{{ detailsError }}</p>
        </div>
        <div v-else class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th v-if="isFriendTarget(batchIssuance)">LINE User ID</th>
                <template v-else>
                  <th>會員姓名</th>
                  <th>電話</th>
                  <th>Email</th>
                </template>
                <th>狀態</th>
                <th>發放數量</th>
                <th>錯誤訊息</th>
                <th>通知結果</th>
                <th>執行時間</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="executionDetails.length === 0">
                <td :colspan="isFriendTarget(batchIssuance) ? 7 : 9" class="empty-state">沒有發送明細</td>
              </tr>
              <tr v-for="detail in executionDetails" :key="detail.id">
                <td v-if="isFriendTarget(batchIssuance)">
                  <code class="uid-badge">{{ detail.lineUserId || '-' }}</code>
                </td>
                <template v-else>
                  <td>{{ detail.memberName || '-' }}</td>
                  <td>{{ detail.memberPhone || '-' }}</td>
                  <td>{{ detail.memberEmail || '-' }}</td>
                </template>
                <td>
                  <span :class="getDetailStatusBadgeClass(detail.status)">
                    {{ getDetailStatusText(detail.status) }}
                  </span>
                </td>
                <td>{{ detail.issuedCount }} / {{ detail.expectedCount }}</td>
                <td>{{ detail.errorMessage || '-' }}</td>
                <td>
                  <div v-if="detail.notificationResults" class="notif-results">
                    <span
                      v-for="(res, ch) in detail.notificationResults"
                      :key="ch"
                      :class="['notif-badge', res.sent ? 'notif-ok' : 'notif-fail']"
                      :title="res.error || res.messageId || ''"
                    >
                      <i :class="chIcon(ch)"></i>
                      {{ chLabel(ch) }}
                      <i :class="res.sent ? 'ri-check-line' : 'ri-close-line'"></i>
                    </span>
                  </div>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>{{ formatDateTime(detail.executedAt) }}</td>
                <td>
                  <button
                    v-if="detail.status === 'success' && detail.memberId"
                    class="btn btn-sm btn-outline-danger"
                    @click="handleViewCoupons(detail)"
                    :disabled="loadingCoupons[detail.id]"
                  >
                    <i class="ri-eye-line"></i>
                    查看優惠券
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- 分頁 -->
          <ListPagination
            v-if="detailsPagination && detailsPagination.totalPages > 1"
            :page="detailsPagination.page"
            :total-pages="detailsPagination.totalPages"
            :total="detailsPagination.totalItems"
            @change="changeDetailsPage"
          />
        </div>
      </div>
    </div>

    <!-- 優惠券列表對話框 -->
    <div v-if="showCouponsDialog" class="modal-overlay" @click.self="showCouponsDialog = false">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3>優惠券列表 - {{ currentMemberDetail?.memberName }}</h3>
          <button class="btn btn-sm btn-secondary" @click="showCouponsDialog = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="currentMemberCoupons.length === 0" class="empty-state">
            沒有優惠券記錄
          </div>
          <div v-else class="coupons-list">
            <div
              v-for="coupon in currentMemberCoupons"
              :key="coupon.id"
              class="coupon-item"
            >
              <div class="coupon-info">
                <div class="coupon-code">{{ coupon.couponCode }}</div>
                <div class="coupon-details">
                  <span class="detail-item">
                    <span class="label">狀態：</span>
                    <span :class="getCouponStatusBadgeClass(coupon.status)">
                      {{ getCouponStatusText(coupon.status) }}
                    </span>
                  </span>
                  <span class="detail-item">
                    <span class="label">發放時間：</span>
                    <span>{{ formatDateTime(coupon.issuedAt) }}</span>
                  </span>
                  <span v-if="coupon.validUntil" class="detail-item">
                    <span class="label">有效期至：</span>
                    <span>{{ formatDateTime(coupon.validUntil) }}</span>
                  </span>
                  <span v-if="coupon.usedAt" class="detail-item">
                    <span class="label">使用時間：</span>
                    <span>{{ formatDateTime(coupon.usedAt) }}</span>
                  </span>
                </div>
              </div>
              <div class="coupon-actions">
                <template v-if="coupon.status === 'used'">
                  <span class="text-muted">已使用，無法刪除</span>
                </template>
                <template v-else>
                  <button
                    class="btn btn-sm btn-danger"
                    @click="handleDeleteCoupon(coupon)"
                    :disabled="deletingCouponId === coupon.id"
                  >
                    <span v-if="deletingCouponId === coupon.id">
                      <i class="ri-loader-4-line spin"></i>
                      刪除中...
                    </span>
                    <span v-else>
                      <i class="ri-delete-bin-line"></i>
                      刪除
                    </span>
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { couponService } from '@/api/modules/coupons.js';
import { showError, showSuccess, showConfirm } from '@/utils/swal.js';

export default {
  name: 'BatchCouponIssuanceDetail',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { pageTitle } = usePageTitle('批次發送優惠券詳情');
    const batchIssuance = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const showDetails = ref(false);
    const executionDetails = ref([]);
    const detailsLoading = ref(false);
    const detailsError = ref(null);
    const detailsPagination = ref(null);
    const detailsSearchQuery = ref('');
    const detailsFilterStatus = ref('');
    const detailsSearchTimeout = ref(null);
    const loadingCoupons = ref({});
    const showCouponsDialog = ref(false);
    const currentMemberCoupons = ref([]);
    const currentMemberDetail = ref(null);
    const deletingCouponId = ref(null);

    const loadBatchIssuance = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await couponService.getBatchIssuance(route.params.batchId);

        if (response && response.success && response.data) {
          batchIssuance.value = response.data;
          // 如果已完成或失敗，自動顯示明細
          if (response.data.status === 'completed' || response.data.status === 'failed') {
            showDetails.value = true;
            loadExecutionDetails();
          }
        } else {
          error.value = response?.message || '獲取批次發放優惠券詳情失敗';
        }
      } catch (err) {
        console.error('獲取批次發放優惠券詳情失敗:', err);
        error.value = err.message || '獲取批次發放優惠券詳情失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const loadExecutionDetails = async () => {
      if (!showDetails.value || !batchIssuance.value) return;

      detailsLoading.value = true;
      detailsError.value = null;

      try {
        const params = {
          page: detailsPagination.value?.page || 1,
          pageSize: 20
        };

        if (detailsSearchQuery.value) {
          params.search = detailsSearchQuery.value;
        }
        if (detailsFilterStatus.value) {
          params.status = detailsFilterStatus.value;
        }

        const response = await couponService.getBatchIssuanceDetails(route.params.batchId, params);

        if (response && response.success && response.data) {
          executionDetails.value = response.data.items || [];
          detailsPagination.value = response.data.pagination;
        } else {
          detailsError.value = response?.message || '獲取發送明細失敗';
        }
      } catch (err) {
        console.error('獲取發送明細失敗:', err);
        detailsError.value = err.message || '獲取發送明細失敗';
      } finally {
        detailsLoading.value = false;
      }
    };

    const handleDetailsSearch = () => {
      if (detailsSearchTimeout.value) {
        clearTimeout(detailsSearchTimeout.value);
      }
      detailsSearchTimeout.value = setTimeout(() => {
        if (detailsPagination.value) {
          detailsPagination.value.page = 1;
        }
        loadExecutionDetails();
      }, 500);
    };

    const handleDetailsFilterChange = () => {
      if (detailsPagination.value) {
        detailsPagination.value.page = 1;
      }
      loadExecutionDetails();
    };

    const changeDetailsPage = (page) => {
      if (detailsPagination.value) {
        detailsPagination.value.page = page;
      }
      loadExecutionDetails();
    };

    watch(showDetails, (newVal) => {
      if (newVal) {
        loadExecutionDetails();
      }
    });

    const handleBack = () => {
      router.push('/client/coupons/issues/batch');
    };

    const getStatusText = (status) => {
      const statusMap = {
        processing: '執行中',
        completed: '已完成',
        failed: '失敗',
        cancelled: '已取消'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        processing: 'badge badge-info',
        completed: 'badge badge-success',
        failed: 'badge badge-danger',
        cancelled: 'badge badge-secondary'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const getDetailStatusText = (status) => {
      return status === 'success' ? '成功' : '失敗';
    };

    const getDetailStatusBadgeClass = (status) => {
      return status === 'success' ? 'badge badge-success' : 'badge badge-danger';
    };

    const chLabel = (ch) => ({ line: 'LINE', email: 'Email', sms: 'SMS' }[ch] || ch);
    const chIcon  = (ch) => ({
      line:  'ri-line-fill',
      email: 'ri-mail-line',
      sms:   'ri-message-2-line'
    }[ch] || 'ri-notification-line');

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };

    const isFriendTarget = (issuance) => {
      return issuance?.targetType === 'all_friends' || issuance?.targetType === 'line_tags';
    };

    const getCreatedByText = (batchIssuance) => {
      if (batchIssuance.createdByName) {
        return batchIssuance.createdByName;
      }
      if (batchIssuance.createdByUsername) {
        return batchIssuance.createdByUsername;
      }
      return '-';
    };

    const handleViewCoupons = async (detail) => {
      if (!detail.memberId || !batchIssuance.value) return;

      loadingCoupons.value[detail.id] = true;
      try {
        const response = await couponService.getCouponsByBatchAndMember(
          route.params.batchId,
          detail.memberId
        );

        if (response && response.success && response.data) {
          // 確保 data 是陣列
          currentMemberCoupons.value = Array.isArray(response.data) ? response.data : [];
          currentMemberDetail.value = detail;
          showCouponsDialog.value = true;
          
          // 調試信息
          console.log('優惠券列表:', currentMemberCoupons.value);
        } else {
          await showError('獲取優惠券記錄失敗', response?.message || '未知錯誤');
        }
      } catch (err) {
        console.error('獲取優惠券記錄失敗:', err);
        await showError('獲取優惠券記錄失敗', err.message || '未知錯誤');
      } finally {
        loadingCoupons.value[detail.id] = false;
      }
    };

    const handleDeleteCoupon = async (coupon) => {
      if (coupon.status === 'used') {
        await showError('無法刪除', '已使用的優惠券無法刪除');
        return;
      }

      const confirmed = await showConfirm(
        '確認刪除',
        `確定要刪除優惠券 ${coupon.couponCode} 嗎？此操作無法復原。`,
        'warning'
      );

      if (!confirmed) return;

      deletingCouponId.value = coupon.id;
      try {
        const response = await couponService.deleteCouponIssue(coupon.id);

        if (response && response.success) {
          await showSuccess('刪除成功', '優惠券已刪除');
          
          // 從列表中移除已刪除的優惠券
          currentMemberCoupons.value = currentMemberCoupons.value.filter(
            c => c.id !== coupon.id
          );
          
          // 如果沒有優惠券了，關閉對話框
          if (currentMemberCoupons.value.length === 0) {
            showCouponsDialog.value = false;
            currentMemberCoupons.value = [];
            currentMemberDetail.value = null;
          }
          
          // 重新載入批次發放優惠券以更新統計
          await loadBatchIssuance();
          // 重新載入發送明細
          if (showDetails.value) {
            await loadExecutionDetails();
          }
        } else {
          await showError('刪除失敗', response?.message || '未知錯誤');
        }
      } catch (err) {
        console.error('刪除優惠券失敗:', err);
        await showError('刪除失敗', err.message || '未知錯誤');
      } finally {
        deletingCouponId.value = null;
      }
    };

    const getCouponStatusText = (status) => {
      const statusMap = {
        unused: '未使用',
        transferring: '轉送中',
        transferred_out: '已轉出',
        used: '已使用',
        expired: '已過期',
        cancelled: '已取消',
        transferred: '已轉讓'
      };
      return statusMap[status] || status;
    };

    const getCouponStatusBadgeClass = (status) => {
      const classMap = {
        unused: 'badge badge-success',
        transferring: 'badge badge-warning',
        transferred_out: 'badge badge-info',
        used: 'badge badge-primary',
        expired: 'badge badge-secondary',
        cancelled: 'badge badge-warning',
        transferred: 'badge badge-info'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    onMounted(() => {
      loadBatchIssuance();
    });

    return {
      pageTitle,
      batchIssuance,
      loading,
      error,
      showDetails,
      executionDetails,
      detailsLoading,
      detailsError,
      detailsPagination,
      detailsSearchQuery,
      detailsFilterStatus,
      loadExecutionDetails,
      handleDetailsSearch,
      handleDetailsFilterChange,
      changeDetailsPage,
      handleBack,
      isFriendTarget,
      getStatusText,
      getStatusBadgeClass,
      getDetailStatusText,
      getDetailStatusBadgeClass,
      formatDateTime,
      getCreatedByText,
      handleViewCoupons,
      loadingCoupons,
      showCouponsDialog,
      currentMemberCoupons,
      currentMemberDetail,
      handleDeleteCoupon,
      deletingCouponId,
      getCouponStatusText,
      getCouponStatusBadgeClass,
      chLabel,
      chIcon
    };
  }
};
</script>

<style scoped>
.page-container {
  padding: 24px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.detail-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.detail-section {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e9ecef;
}

.detail-section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.info-list {
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

.info-row {
  display: flex;
  align-items: baseline;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row:nth-child(even) {
  background: #fafafa;
}

.info-list dt {
  flex: 0 0 160px;
  font-size: 13px;
  font-weight: 600;
  color: #666;
  padding-right: 16px;
}

.info-list dd {
  flex: 1;
  margin: 0;
  font-size: 14px;
  color: #333;
}

.divider {
  margin: 0 8px;
  color: #ccc;
}

.revoke-section .section-title {
  color: #856404;
}

.revoke-section {
  background: #fffbf0;
  border-radius: 6px;
  padding: 20px;
  border: 1px solid #ffe69c;
}

.revoke-count {
  font-weight: 600;
  color: #856404;
  margin-right: 8px;
}

.revoke-note {
  font-size: 12px;
  color: #999;
}

.code-badge {
  background: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
}

.uid-badge {
  background: #e8f4fd;
  color: #0070c0;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
}

.progress-bar {
  position: relative;
  width: 200px;
  height: 24px;
  background: #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #28a745;
  transition: width 0.3s;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  font-weight: 600;
  color: #333;
}

.detail-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.details-toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.search-box input {
  padding-right: 40px;
}

.search-box i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

.table-wrapper {
  margin-top: 16px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: #f8f9fa;
}

th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e9ecef;
}

td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
}

tbody tr:hover {
  background: #f8f9fa;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.text-success {
  color: #28a745;
}

.text-danger {
  color: #dc3545;
}

.error-text {
  color: #dc3545;
  font-size: 14px;
}

/* 對話框樣式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.coupons-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.coupon-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f8f9fa;
}

.coupon-info {
  flex: 1;
}

.coupon-code {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-family: monospace;
}

.coupon-details {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 14px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.detail-item .label {
  color: #666;
  font-weight: 500;
}

.coupon-actions {
  display: flex;
  align-items: center;
}

.text-muted {
  color: #999;
  font-size: 14px;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* 通知結果徽章 */
.notif-results {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.notif-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 500;
  white-space: nowrap;
}

.notif-ok {
  background: #e6f7ee;
  color: #1a7a43;
}

.notif-fail {
  background: #fff0f0;
  color: #c0392b;
}
</style>
